import { useUniversalModalDispatch } from '@components/context/UniversalModalContext';
import { setHeaders } from '@graphql/graphql-client/graphClient';
import { useMutation, useQueryClient } from 'react-query';
import { useUserSession } from './useDefaultSession';
import { useQueryParams } from '../useQueryParams';
import { useInvalidateQueries } from '@hooks/useInvalidateQueries';
import { SideBarStates, useSidebarDispatch } from '@components/context/SidebarContext';
import { useRouter } from 'next/router';

/**
 * This hook is specifically wrapped with Next API routes.
 * We need to updated the Secure Session Cookie that's HTTP only with the new returned JWT.
 * The JWT contains 2 users ID's [currentAccount, accountOwner], if these match then its the Account Admin account.
 * If the 2 JWT's do not match, then its an impersonated User account
 *
 * @returns
 */
export function useImpersonateUser(redirect=false) {
    const queryClient = useQueryClient();
    const invalidateQueries = useInvalidateQueries();
    const { toggle: sidebarToggle } = useSidebarDispatch();
    const router = useRouter();
    return useMutation(
        async ({ id }: { id: string }) => {
            const body = JSON.stringify({
                id,
            });
            const resp = await fetch('/api/impersonate', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body,
            });
            const jbody = await resp.json();
            if (resp.ok) {
                return jbody;
            }
            throw jbody;
        },
        {
            onSuccess: async () => {
                sidebarToggle(SideBarStates.Closed);
                await invalidateQueries('session');
                await invalidateQueries(
                    'CourseUnitActivitiesData',
                    'CourseUnitsData',
                    'CoursesWithActivitiesAndSubmissions',
                    'CoursesWithActivitiesAndSubmissionsData',
                    'CoursesActivitySubmissionsByEnrollments',
                    'StudentCurrentAverage'
                );
                if (redirect) router.push('/');
            },
        },
    );
}

export function useSwitchEnrollment() {
    const queryClient = useQueryClient();
    const router = useRouter();
    const invalidateQueries = useInvalidateQueries();
    const { dispatchNotification } = useUniversalModalDispatch();    

    return useMutation(
        async ({ id }: { id: string }) => {
            const body = JSON.stringify({
                id,
            });
            const resp = await fetch('/api/enrollment', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body,
            });
            const jbody = await resp.json();
            if (resp.ok) {
                return jbody;
            }
            throw jbody;
        },
        {
            onSuccess: async (data) => {
                if (data?.switchEnrollment?.accessToken) {
                    await invalidateQueries(
                        'CourseMetaData',
                        'courseEnrollments',
                        'CourseUnitActivitiesData',
                        'CourseUnitsData',
                        'CoursesWithActivitiesAndSubmissions',
                        'CoursesActivitySubmissionsByEnrollments',
                    );
                    removeQueryParams(router);
                } else {
                    let errorMessage =
                        data?.data?.switchEnrollment?.errors?.[0] ?? 'Unexpected error returned';
                    dispatchNotification({
                        title: 'Failed to switch enrollment',
                        text: errorMessage,
                        type: 'danger',
                    });
                }
            },
            onError: (data: any) => {
                let errorMessage =
                    (data?.data?.switchEnrollment?.errors?.[0] ||
                        data?.response?.errors?.[0]?.message) ??
                    'Unexpected error returned';
                dispatchNotification({
                    title: 'Failed to switch enrollment',
                    text: errorMessage,
                    type: 'danger',
                });
            },
        },
    );
}

export function useAutoSwitchEnrollment() {
    const invalidateQueries = useInvalidateQueries();
    const router = useRouter();
    return useMutation(
        async ({ id }: { id: string }) => {
            // console.log(' ######### MUTATION ##########')
            const body = JSON.stringify({
                id,
            });
            const resp = await fetch('/api/enrollment', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body,
            });
            const jbody = await resp.json();
            if (resp.ok) {
                return jbody;
            }
            throw jbody;
        },
        {
            onSuccess: async (data) => {
                // console.log('########################ON SUCCESS MUTATION ##############################')    
                if (data?.switchEnrollment?.accessToken) {
                    await invalidateQueries(
                        'CourseMetaData',
                        'courseEnrollments',
                        'CourseUnitActivitiesData',
                        'CourseUnitsData',
                        'CoursesWithActivitiesAndSubmissions',
                        'CoursesActivitySubmissionsByEnrollments',
                    );
                    removeQueryParams(router);
                } else {
                    let errorMessage =
                        data?.data?.switchEnrollment?.errors?.[0] ?? 'Unexpected error returned';
                }
            },
            onError: (data: any) => {
                let errorMessage =
                    (data?.data?.switchEnrollment?.errors?.[0] ||
                        data?.response?.errors?.[0]?.message) ??
                    'Unexpected error returned';
            },
        },
    );
}

const removeQueryParams = (router) => {
    const currentQuery = { ...router.query };

    delete currentQuery.role;

    router.replace(
        {
            pathname: router.pathname,
            query: currentQuery,
        },
        undefined,
        { shallow: true },
    );
};

